import { useAlert } from '@ads/components-react';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { type EViewPortEnum, getApiV1SsoimageIdQueryKey } from '@/shared/api/generated';
import { useDeleteApiV1SsoimageIdVideo } from '@/shared/api/generated/hooks/SSOImageController/useDeleteApiV1SsoimageIdVideo';
import { usePostApiV1SsoimageIdVideo } from '@/shared/api/generated/hooks/SSOImageController/usePostApiV1SsoimageIdVideo';
import { useSlideEditorContext } from '../context/SlideEditorContext';

/** Tamanho máximo aceito no cliente antes do upload (o limite real é definido pelo backend). */
export const MAX_VIDEO_SIZE_MB = 20;

/**
 * Gerencia o vídeo de fundo da variante ativa (idioma × dispositivo) do slide.
 *
 * Encapsula os hooks gerados pelo Kubb de upload (`POST /ssoimage/{id}/video`) e
 * remoção (`DELETE /ssoimage/{id}/video`), mapeando idioma → `CultureInfo` e
 * dispositivo → `ViewPort`. Em caso de sucesso, sincroniza o canvas via contexto
 * e invalida a query do slide.
 *
 * @returns Ações `upload`/`remove` e flags de carregamento.
 *
 * @example
 * ```tsx
 * const { upload, remove, isUploading } = useSlideVideoBackground();
 * await upload(file);
 * ```
 */
export function useSlideVideoBackground() {
  const ctx = useSlideEditorContext();
  const queryClient = useQueryClient();
  const { alert } = useAlert();
  const { t } = useTranslation('slideEditor');

  const { mutateAsync: uploadVideo, isPending: isUploading } = usePostApiV1SsoimageIdVideo();
  const { mutateAsync: deleteVideo, isPending: isRemoving } = useDeleteApiV1SsoimageIdVideo();

  const cultureInfo = ctx.activeLanguage;
  const viewPort: EViewPortEnum = ctx.activeDevice === 'desktop' ? 1 : 2;
  const slideId = ctx.slideId;

  const invalidateSlide = useCallback(() => {
    if (slideId == null) return;
    void queryClient.invalidateQueries({ queryKey: getApiV1SsoimageIdQueryKey(slideId) });
  }, [queryClient, slideId]);

  const upload = useCallback(
    async (file: File): Promise<void> => {
      if (slideId == null) {
        alert({ alertType: 'danger', title: t('toolbar.backgroundVideoNoSlide') });
        return;
      }
      if (file.size > MAX_VIDEO_SIZE_MB * 1024 * 1024) {
        alert({
          alertType: 'warning',
          title: t('toolbar.backgroundVideoTooLarge', { size: MAX_VIDEO_SIZE_MB }),
        });
        return;
      }
      try {
        const res = await uploadVideo({
          id: slideId,
          data: { File: file, CultureInfo: cultureInfo, ViewPort: viewPort },
        });
        if (res?.url) {
          ctx.setBackgroundVideo(res.url);
          invalidateSlide();
        }
      } catch {
        alert({ alertType: 'danger', title: t('toolbar.backgroundVideoUploadError') });
      }
    },
    [slideId, uploadVideo, cultureInfo, viewPort, ctx, alert, t, invalidateSlide],
  );

  const remove = useCallback(async (): Promise<void> => {
    if (slideId == null) {
      ctx.removeBackgroundVideo();
      return;
    }
    try {
      await deleteVideo({ id: slideId, data: { cultureInfo, viewPort } });
      ctx.removeBackgroundVideo();
      invalidateSlide();
    } catch {
      alert({ alertType: 'danger', title: t('toolbar.backgroundVideoRemoveError') });
    }
  }, [slideId, deleteVideo, cultureInfo, viewPort, ctx, alert, t, invalidateSlide]);

  return { upload, remove, isUploading, isRemoving } as const;
}
